import React, { useEffect, useRef, useState } from 'react';
import {
  Tiktok,
  Instagram,
  CheckCircleFill,
  HourglassSplit,
  ExclamationCircleFill,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { applyCreator } from '../api/creators';
import './CreatorTabs.scss';

// Shown when the logged-in user is NOT an approved creator. Collects socials +
// a short note and submits the application via POST /creators/apply. Every
// field is required; the server enforces the same (see apply_creator.js).
//
// The two declarations from #597 (the ÁSZF, and having 10k+ followers on one
// platform) are a front-end gate only: they block this form from submitting,
// and are deliberately not sent to or stored by the API. Nothing downstream
// knows whether they were ticked — the record is the applicant's click and
// nothing else.
//
// The `pending` prop (from GET /creators/me status 'pending') and the local
// `applied` state both drive the "under review" screen; on a successful submit
// we call onApplied() so the Dashboard re-fetches the real status.

// Drives the error summary and which input we focus when a submit is blocked:
// always the topmost offender, the way a browser's own validation behaves.
// The declarations come last because that is where they sit on the form: you
// agree to something after reading it, not before.
const FIELD_ORDER = ['tiktok', 'instagram', 'note', 'terms', 'followers'];

// The ÁSZF the first checkbox accepts. Same destination as the footer's legal
// links until a dedicated terms page exists.
const TERMS_URL = 'https://outfino.io';

export default function ApplicationForm({ pending = false, onApplied }) {
  const { t } = useLocale();
  const [tiktok, setTiktok] = useState('');
  const [instagram, setInstagram] = useState('');
  const [note, setNote] = useState('');
  const [terms, setTerms] = useState(false);
  const [followers, setFollowers] = useState(false);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(false);

  // Per-field messages, plus the latch that decides whether they are visible.
  // Nothing is flagged before the first submit: telling someone a field is
  // empty when they have not reached it yet is noise, not help.
  const [fieldErrors, setFieldErrors] = useState({});
  const [validated, setValidated] = useState(false);

  const refs = {
    tiktok: useRef(null),
    instagram: useRef(null),
    note: useRef(null),
    terms: useRef(null),
    followers: useRef(null),
  };

  function collectErrors() {
    const found = {};
    // One platform is enough, and which one is the applicant's choice — so
    // the requirement belongs to the pair, not to either field. The message
    // hangs off the first of the two, which is also where the focus jump
    // lands (see FIELD_ORDER).
    if (!tiktok.trim() && !instagram.trim()) found.tiktok = t('apply.errSocial');
    if (!note.trim()) found.note = t('apply.errNote');
    if (!terms) found.terms = t('apply.errTerms');
    if (!followers) found.followers = t('apply.errFollowers');
    return found;
  }

  // Once the messages are on screen, keep them in step with what is typed so a
  // field clears the moment it is filled, rather than on the next submit.
  useEffect(() => {
    if (validated) setFieldErrors(collectErrors());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiktok, instagram, note, terms, followers, validated]);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');

    const found = collectErrors();
    setFieldErrors(found);
    setValidated(true);

    const firstInvalid = FIELD_ORDER.find((name) => found[name]);
    if (firstInvalid) {
      refs[firstInvalid].current?.focus();
      return;
    }

    setSubmitting(true);
    try {
      await applyCreator({
        tiktok: tiktok.trim(),
        instagram: instagram.trim(),
        note: note.trim(),
      });
      setApplied(true);
      // Let the Dashboard re-fetch /creators/me so the real status ('pending')
      // takes over.
      if (onApplied) onApplied();
    } catch (e2) {
      setErr(t('apply.error'));
    } finally {
      setSubmitting(false);
    }
  }

  // Shared wiring for every control: the ref the focus jump needs, and the
  // aria link from the input to the message describing what is wrong with it.
  function controlProps(name, { required = true } = {}) {
    const invalid = Boolean(fieldErrors[name]);
    return {
      ref: refs[name],
      required,
      'aria-invalid': invalid,
      'aria-describedby': invalid ? `apply-err-${name}` : undefined,
    };
  }

  function fieldClass(name) {
    return `apply-field${fieldErrors[name] ? ' is-invalid' : ''}`;
  }

  function checkClass(name) {
    return `apply-check${fieldErrors[name] ? ' is-invalid' : ''}`;
  }

  function fieldError(name) {
    if (!fieldErrors[name]) return null;
    return (
      <span className="apply-field__error" id={`apply-err-${name}`} role="alert">
        <ExclamationCircleFill size={13} />
        {fieldErrors[name]}
      </span>
    );
  }

  if (pending || applied) {
    return (
      <div className="apply-wrap">
        <div className="apply-pending">
          <span className="apply-pending__icon">
            <HourglassSplit size={30} />
          </span>
          <span className="apply-pending__badge">{t('apply.pendingBadge')}</span>
          <h1 className="apply-pending__title">{t('apply.pendingTitle')}</h1>
          <p className="apply-pending__body">{t('apply.pendingBody')}</p>
        </div>
      </div>
    );
  }

  const blocked = validated && Object.keys(fieldErrors).length > 0;

  // The whole application gates the submit button: both socials, the note and
  // both declarations. Until every one of them is in there is nothing worth
  // sending, so the control says so up front rather than taking the click and
  // answering with an error.
  //
  // Derived from collectErrors() rather than from its own list of conditions,
  // so the gate and the per-field messages can never disagree about what the
  // form requires. It also matches what the API accepts — it wants both
  // handles and the note (see apply_creator.js), so an enabled button always
  // means a submit that will go through.
  const complete = Object.keys(collectErrors()).length === 0;

  return (
    <div className="apply-wrap">
      <div className="apply-card">
        <h1 className="apply-card__title">{t('apply.title')}</h1>
        <p className="apply-card__subtitle">{t('apply.subtitle')}</p>

        <div className="apply-req">
          <CheckCircleFill size={15} />
          {t('apply.req')}
        </div>

        {/* noValidate: the browser's native bubbles are unstyled, untranslated
            and disappear on the next click. Our own messages stay put, sit
            with the field they belong to, and follow the locale. */}
        <form className="apply-form" onSubmit={handleSubmit} noValidate>
          <label className={fieldClass('tiktok')}>
            <span className="apply-field__label">
              <Tiktok size={15} /> {t('apply.tiktok')}
            </span>
            <input
              type="text"
              value={tiktok}
              onChange={(e) => setTiktok(e.target.value)}
              placeholder={t('apply.tiktokPlaceholder')}
              autoComplete="off"
              {...controlProps('tiktok', { required: false })}
            />
            {fieldError('tiktok')}
          </label>

          <label className={fieldClass('instagram')}>
            <span className="apply-field__label">
              <Instagram size={15} /> {t('apply.instagram')}
            </span>
            <input
              type="text"
              value={instagram}
              onChange={(e) => setInstagram(e.target.value)}
              placeholder={t('apply.instagramPlaceholder')}
              autoComplete="off"
              {...controlProps('instagram', { required: false })}
            />
            {fieldError('instagram')}
          </label>

          <label className={fieldClass('note')}>
            <span className="apply-field__label">
              {t('apply.note')}
            </span>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('apply.notePlaceholder')}
              {...controlProps('note')}
            />
            {fieldError('note')}
          </label>

          {/* The two declarations (#597). They sit below the fields and above
              the summary, so the last thing read before submitting is what is
              being agreed to. */}
          <div className="apply-checks">
            <div className={checkClass('terms')}>
              <div className="apply-check__row">
                <input
                  type="checkbox"
                  id="apply-terms"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                  {...controlProps('terms')}
                />
                <label className="apply-check__text" htmlFor="apply-terms">
                  {t('apply.termsBefore')}{' '}
                  {/* Without stopping the click, following the link would also
                      tick the box it sits inside — reading the terms and
                      accepting them would become one gesture. */}
                  <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('apply.termsLink')}
                  </a>
                  {t('apply.termsAfter')}
                </label>
              </div>
              {fieldError('terms')}
            </div>

            <div className={checkClass('followers')}>
              <div className="apply-check__row">
                <input
                  type="checkbox"
                  id="apply-followers"
                  checked={followers}
                  onChange={(e) => setFollowers(e.target.checked)}
                  {...controlProps('followers')}
                />
                <label className="apply-check__text" htmlFor="apply-followers">
                  {t('apply.followersDeclaration')}
                </label>
              </div>
              {fieldError('followers')}
            </div>
          </div>

          {(blocked || err) && (
            <div className="apply-form__error" role="alert">
              <ExclamationCircleFill size={15} />
              <span>{err || t('apply.needAll')}</span>
            </div>
          )}

          <button
            type="submit"
            className="btn-o btn-o--brand apply-form__submit"
            disabled={submitting || !complete}
          >
            {submitting ? t('apply.submitting') : t('apply.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
